import { useMemo, useState } from 'react'
import { Tag as TagIcon, X } from 'lucide-react'
import { collectTags } from '@trackmind/core'
import type { Task } from '@/types'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * Browse by tag.
 *
 * Tags have been written from the start — the auto-tagger applies them, quick
 * capture parses them, cards render them — and until now there was no way to
 * read one back. This is the missing half: every tag in use, how much is
 * filed under it, and a tap to see just that.
 *
 * Selecting a second tag narrows rather than widens, which is what a second
 * filter is expected to do. A parent tag brings its children, so `Work`
 * includes `Work/Deep`.
 *
 * The row scrolls sideways inside itself rather than wrapping to five lines
 * on a phone — but it is its own scroll container, so the *page* never scrolls
 * sideways, which is the thing that makes iOS zoom out.
 */
export function TagFilterBar({
  tasks,
  selected,
  onChange,
  className,
}: {
  tasks: Task[]
  selected: string[]
  onChange: (next: string[]) => void
  className?: string
}) {
  const [showAll, setShowAll] = useState(false)
  const tags = useMemo(() => collectTags(tasks), [tasks])

  if (tags.length === 0) return null

  // Long tails of one-off tags aren't worth the room until asked for.
  const TOP = 8
  const shown = showAll ? tags : tags.slice(0, TOP)
  const hidden = tags.length - shown.length

  const toggle = (tag: string) => {
    onChange(selected.includes(tag) ? selected.filter((t) => t !== tag) : [...selected, tag])
  }

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-center gap-2">
        <TagIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <div className="-mx-1 flex flex-1 gap-1.5 overflow-x-auto px-1 pb-1">
          {shown.map((tag) => {
            const active = selected.includes(tag.tag)
            return (
              <button
                key={tag.tag}
                type="button"
                onClick={() => toggle(tag.tag)}
                aria-pressed={active}
                className={cn(
                  'flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors',
                  active
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card hover:bg-accent',
                )}
                title={tag.tag}
              >
                <span className="max-w-[10rem] truncate">{tag.label}</span>
                <span className={cn('tabular-nums', active ? 'opacity-80' : 'text-muted-foreground')}>
                  {tag.open > 0 ? tag.open : tag.count}
                </span>
              </button>
            )
          })}
          {hidden > 0 && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="shrink-0 rounded-full border border-dashed px-2.5 py-1 text-xs text-muted-foreground hover:bg-accent"
            >
              +{hidden} more
            </button>
          )}
        </div>
        {selected.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 px-2 text-xs"
            onClick={() => onChange([])}
          >
            <X className="mr-1 h-3 w-3" />
            Clear
          </Button>
        )}
      </div>
      {selected.length > 1 && (
        <p className="text-xs text-muted-foreground">
          Showing tasks tagged {selected.join(' and ')}.
        </p>
      )}
    </div>
  )
}
